import React from 'react';
import { STORAGE_URL } from '../../services/api';

const CertificatePrint = ({ data = {}, photos = {}, language = 'marathi', stamp = null, logo = null, authoritySignature = null }) => {
    // Default to showing both Marathi and English (matching backend template)
    const showMarathi = language === 'marathi' || language === 'both' || language === 'english';
    const showEnglish = language === 'english' || language === 'both' || language === 'marathi';

    // Helper: Convert English numerals to Marathi
    const toMarathiNumerals = (num) => {
        if (num === null || num === undefined || num === '') return '-';
        const marathiDigits = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
        return String(num).replace(/[0-9]/g, (d) => marathiDigits[parseInt(d)]);
    };

    // Helper: Format date as dd/mm/yyyy in Marathi numerals
    const toMarathiDateShort = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return toMarathiNumerals(`${day}/${month}/${year}`);
        } catch {
            return '-';
        }
    };

    // Helper: Format date as dd/mm/yyyy
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
        } catch {
            return '-';
        }
    };

    // Helper: Check if text is already in Devanagari
    const isDevanagari = (text) => {
        if (!text) return false;
        return /[ऀ-ॿ]/.test(text);
    };

    // Helper: Transliterate English text to Marathi
    const transliterateToMarathi = (text) => {
        if (!text) return '';
        if (isDevanagari(text)) return text; // Already in Marathi

        // Common Maharashtrian names dictionary
        const commonNames = {
            // Male names
            'Rajesh': 'राजेश', 'Suresh': 'सुरेश', 'Mahesh': 'महेश', 'Dinesh': 'दिनेश',
            'Ramesh': 'रमेश', 'Ganesh': 'गणेश', 'Mangesh': 'मंगेश', 'Sandesh': 'संदेश',
            'Prakash': 'प्रकाश', 'Vikram': 'विक्रम', 'Sachin': 'सचिन', 'Rahul': 'राहुल',
            'Amit': 'अमित', 'Sunil': 'सुनील', 'Anil': 'अनिल', 'Vijay': 'विजय',
            'Sanjay': 'संजय', 'Deepak': 'दीपक', 'Dipak': 'दिपक', 'Nilesh': 'निलेश',
            'Datta': 'दत्ता', 'Eknath': 'एकनाथ', 'Krishna': 'कृष्णा', 'Gopal': 'गोपाळ',
            'Shiva': 'शिवा', 'Shankar': 'शंकर', 'Vishnu': 'विष्णू', 'Mohan': 'मोहन',
            'Rohan': 'रोहन', 'Sohan': 'सोहन', 'Ashok': 'अशोक', 'Vasant': 'वसंत',
            'Kiran': 'किरण', 'Shree': 'श्री', 'Shri': 'श्री', 'Shreyas': 'श्रेयस',
            'Sagar': 'सागर', 'Ravi': 'रवि', 'Ajay': 'अजय', 'Vivek': 'विवेक',
            'Prasad': 'प्रसाद', 'Umesh': 'उमेश', 'Ramesh': 'रमेश', 'Santosh': 'संतोष',
            'Anand': 'आनंद', 'Mukesh': 'मुकेश', 'Rakesh': 'राकेश', 'Naresh': 'नरेश',
            'Paresh': 'परेश', 'Bhavesh': 'भावेश', 'Dhiren': 'धीरेन', 'Jiten': 'जितेन',
            // Female names
            'Priya': 'प्रिया', 'Pooja': 'पूजा', 'Puja': 'पूजा', 'Sneha': 'स्नेहा',
            'Neha': 'नेहा', 'Rekha': 'रेखा', 'Meena': 'मीना', 'Mina': 'मीना',
            'Leela': 'लीला', 'Lila': 'लीला', 'Usha': 'उषा', 'Sunita': 'सुनीता',
            'Anita': 'अनिता', 'Geeta': 'गीता', 'Gita': 'गीता', 'Seeta': 'सीता',
            'Sita': 'सीता', 'Radha': 'राधा', 'Kavita': 'कविता', 'Savita': 'सविता',
            'Anjali': 'अंजली', 'Shweta': 'श्वेता', 'Sweta': 'श्वेता', 'Bhavna': 'भावना',
            'Pratima': 'प्रतिमा', 'Pratiksha': 'प्रतीक्षा', 'Madhuri': 'माधुरी',
            'Kalpana': 'कल्पना', 'Alka': 'अलका', 'Shanti': 'शांती', 'Pallavi': 'पल्लवी',
            'Deepa': 'दीपा', 'Dipa': 'दीपा', 'Jyoti': 'ज्योती', 'Manisha': 'मनिषा',
            'Nisha': 'निशा', 'Rita': 'रीता', 'Mona': 'मोना', 'Seema': 'सीमा',
            'Roshni': 'रोशनी', 'Komal': 'कोमल', 'Priti': 'प्रीती', 'Asha': 'आशा',
            // Common words
            'Sai': 'साई', 'Vaidik': 'वैदिक', 'Vivah': 'विवाह', 'President': 'अध्यक्ष',
            'Shree': 'श्री', 'Shri': 'श्री', 'Saibaba': 'साईबाबा',
            // Place names
            'Nashik': 'नाशिक', 'nashik': 'नाशिक', 'NASHIK': 'नाशिक',
            'Mumbai': 'मुंबई', 'Pune': 'पुणे', 'Nagpur': 'नागपूर',
            'Kolhapur': 'कोल्हापूर', 'Solapur': 'सोलापूर', 'Aurangabad': 'औरंगाबाद',
            'Thane': 'ठाणे', 'Satara': 'सातारा', 'Ratnagiri': 'रत्नागिरी',
            'Jalgaon': 'जळगाव', 'Ahmednagar': 'अहमदनगर', 'Sangli': 'सांगली',
            'Dhule': 'धुळे', 'dhule': 'धुळे', 'Dhulia': 'धुळे',
            'Malegaon': 'मालेगाव', 'Nasik': 'नाशिक',
        };

        // Check if it's a known name
        if (commonNames[text]) return commonNames[text];

        // Basic transliteration for unknown names
        return basicTransliterate(text);
    };

    // Basic transliteration rules
    const basicTransliterate = (text) => {
        if (!text) return '';

        // Vowel mappings
        const vowels = {
            'a': 'अ', 'aa': 'आ', 'A': 'अ', 'AA': 'आ',
            'i': 'इ', 'ee': 'ई', 'I': 'इ', 'II': 'ई',
            'u': 'उ', 'oo': 'ऊ', 'U': 'उ', 'UU': 'ऊ',
            'e': 'ए', 'E': 'ए', 'ai': 'ऐ',
            'o': 'ओ', 'O': 'ओ', 'au': 'औ', 'ou': 'औ'
        };

        // Consonant mappings
        const consonants = {
            'k': 'क', 'kh': 'ख', 'g': 'ग', 'gh': 'घ',
            'c': 'च', 'ch': 'छ', 'j': 'ज', 'jh': 'झ',
            'T': 'ट', 'Th': 'ठ', 'D': 'ड', 'Dh': 'ढ', 'N': 'ण',
            't': 'त', 'th': 'थ', 'd': 'द', 'dh': 'ध', 'n': 'न',
            'p': 'प', 'f': 'फ', 'ph': 'फ', 'b': 'ब', 'bh': 'भ', 'm': 'म',
            'y': 'य', 'r': 'र', 'l': 'ल', 'v': 'व', 'w': 'व',
            'sh': 'श', 's': 'स', 'h': 'ह',
            'K': 'क', 'Kh': 'ख', 'G': 'ग', 'Gh': 'घ',
            'C': 'च', 'Ch': 'छ', 'J': 'ज', 'Jh': 'झ',
            'S': 'स', 'Sh': 'श', 'R': 'र', 'L': 'ल', 'M': 'म', 'Y': 'य', 'V': 'व', 'W': 'व', 'H': 'ह'
        };

        // Matra (vowel signs)
        const matras = {
            'a': '', 'aa': 'ा', 'i': 'ि', 'ee': 'ी', 'u': 'ु', 'oo': 'ू',
            'e': 'े', 'ai': 'ै', 'o': 'ो', 'au': 'ौ', 'ou': 'ौ'
        };

        let result = '';
        let i = 0;
        const len = text.length;

        while (i < len) {
            // Try to match consonant + vowel pattern
            let matched = false;

            for (let j = Math.min(4, len - i); j >= 1; j--) {
                const substr = text.substring(i, i + j);

                // Check for consonants
                if (consonants[substr]) {
                    // Check if followed by vowel/matra
                    if (i + j < len) {
                        const nextChar = text[i + j];
                        if (matras[nextChar] !== undefined) {
                            result += consonants[substr] + matras[nextChar];
                            i += j + 1;
                            matched = true;
                            break;
                        } else if (nextChar === 'a' || nextChar === 'A') {
                            result += consonants[substr];
                            i += j + 1;
                            matched = true;
                            break;
                        }
                    }
                    result += consonants[substr];
                    i += j;
                    matched = true;
                    break;
                }

                // Check for standalone vowels
                if (vowels[substr]) {
                    result += vowels[substr];
                    i += j;
                    matched = true;
                    break;
                }
            }

            if (!matched) {
                result += text[i];
                i++;
            }
        }

        return result;
    };

    // Helper: Get Marathi text from stored field or transliterate
    const getMarathiText = (field) => {
        // Try to get the Marathi field first (e.g., husband_name_mr)
        const marathiField = `${field}_mr`;
        if (data[marathiField] && data[marathiField].trim() !== '') {
            return data[marathiField];
        }

        // Fallback to English field and transliterate
        const englishValue = data[field] || '';
        return transliterateToMarathi(englishValue);
    };

    // Resolve image URLs
    const resolveUrl = (value) => {
        if (!value) return null;
        if (typeof value === 'string') {
            if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:')) return value;
            return `${STORAGE_URL}/${value}`;
        }
        try { return URL.createObjectURL(value); } catch { return null; }
    };

    const husbandPhotoUrl = resolveUrl(photos?.husbandPhoto || data.husband_photo);
    const wifePhotoUrl = resolveUrl(photos?.wifePhoto || data.wife_photo);
    const husbandSigUrl = resolveUrl(photos?.husbandSignature || data.husband_signature);
    const wifeSigUrl = resolveUrl(photos?.wifeSignature || data.wife_signature);
    const stampUrl = resolveUrl(stamp);
    const logoUrl = resolveUrl(logo);
    const authoritySignatureUrl = resolveUrl(authoritySignature);

    // Styles matching backend template exactly
    const styles = {
        certificate: {
            position: 'relative',
            boxSizing: 'border-box',
            width: '100%',
            background: '#ffffff',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
            marginTop:'24px'
        },
        borderOuter: {
            border: '3px solid #c9971f',
            padding: '2mm',
            background: '#ffffff',
        },
        borderInner: {
            border: '1.5px solid #c9971f',
            padding: '4mm',
            background: '#ffffff',
        },
        topBand: {
            textAlign: 'center',
            border: 'none',
            padding: 0,
            background: '#ffffff',
        },
        titleMarathi: {
            fontSize: '22px',
            fontWeight: 700,
            color: '#1a3a6b',
            lineHeight: 1.2,
            marginTop: 0,
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        titleEnglish: {
            fontSize: '22px',
            fontWeight: 700,
            color: '#1a3a6b',
            letterSpacing: '0.5px',
            marginTop: '3px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        sub: {
            fontSize: '13px',
            color: '#1a3a6b',
            marginTop: '1px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        regNo: {
            fontSize: '13px',
            color: '#1a3a6b',
            fontWeight: 'bold',
            marginTop: '2px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        centerDivider: {
            width: '220px',
            margin: '3px auto',
            borderTop: '1px solid #c9971f',
        },
        goldDivider: {
            height: '1px',
            background: '#c9971f',
            margin: '4px 0 0',
        },
        certNumRow: {
            fontSize: '13px',
            color: '#1a3a6b',
            padding: '3px 0',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: 'bold',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        photoTable: {
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: '6px',
        },
        photoCellLeft: {
            width: '50%',
            textAlign: 'left',
            verticalAlign: 'top',
        },
        photoCellRight: {
            width: '50%',
            textAlign: 'left',
            paddingLeft:'222px',
            verticalAlign: 'top',
        },
        photoContainer: {
            position: 'relative',
            width: '85px',
            height: '100px',
            display: 'inline-block',
        },
        photoFrame: {
            width: '140px',
            height: '150px',
            border: '1.5px solid #c9971f',
            background: '#ffffff',
            textAlign: 'center',
            lineHeight: '100px',
            overflow: 'hidden',
        },
        stampOnPhotoRight: {
            position: 'absolute',
            width: '103px',
            height: '103px',
            right: '-113px',
            top: '75%',
            transform: 'translateY(-50%)',
            zIndex: 999,
        },
        stampOnPhotoLeft: {
            position: 'absolute',
            width: '103px',
            height: '103px',
            left: '-58px',
            top: '75%',
            transform: 'translateY(-50%)',
            zIndex: 999,
        },
        photoCaption: {
            fontSize: '12.5px',
            color: '#1a3a6b',
            marginTop: '4px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        sigBox: {
            width: '95px',
            height: '24px',
            borderBottom: '1.5px solid #9c7a2e',
            margin: '5px auto 2px',
            textAlign: 'center',
            overflow: 'hidden',
            background: 'transparent',
        },
        sigCaption: {
            fontSize: '9px',
            color: '#1a3a6b',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        marathiBlock: {
            fontSize: '13px',
            lineHeight: 1.6,
            padding: '6px 8px',
            color: '#1a3a6b',
            border: '1px solid #c9971f',
            background: '#ffffff',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        fl: {
            color: '#1a3a6b',
            borderBottom: '1px solid #1a3a6b',
            display: 'inline-block',
            minWidth: '45px',
            fontWeight: 'bold',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
            paddingBottom: '1px',
        },
        fieldRow: {
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'nowrap',
            width: '100%',
            marginBottom: '2px',
        },
        labelText: {
            whiteSpace: 'nowrap',
            flexShrink: 0,
        },
        labelInline: {
            marginLeft: 'auto',
            paddingLeft: '14px',
            flexShrink: 0,
            whiteSpace: 'nowrap',
        },
        flShort: {
            color: '#1a3a6b',
            borderBottom: '1px solid #1a3a6b',
            display: 'inline-block',
            width: '110px',
            flexShrink: 0,
            fontWeight: 'bold',
            padding: '0 6px',
            margin: '0 4px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        flWide: {
            color: '#1a3a6b',
            borderBottom: '1px solid #1a3a6b',
            display: 'inline-block',
            flex: '1 1 auto',
            minWidth: '320px',
            fontWeight: 'bold',
            padding: '0 6px',
            margin: '0 6px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        flWideO: {
            color: '#1a3a6b',
            borderBottom: '1px solid #1a3a6b',
            display: 'inline-block',
            flex: '1 1 auto',
            minWidth: '250px',
            fontWeight: 'bold',
            padding: '0 6px',
            margin: '0 6px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        textLine: {
            marginBottom: '4px',
        },
        engLine: {
            fontWeight: 700,
        },
        bottomTable: {
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '4px',
            tableLayout: 'fixed',
            background: '#ffffff',
        },
        detailLabel: {
            fontSize: '14px',
            color: '#1a3a6b',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        detailValue: {
            fontWeight: 'bold',
            fontSize: '14px',
            display: 'inline-block',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
            color: '#1a3a6b',
        },
        qrBox: {
            width: '65px',
            height: '65px',
            border: '1px solid #1a3a6b',
            background: '#ffffff',
            marginTop: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        qrLabel: {
            fontSize: '10.5px',
            color: '#1a3a6b',
            marginTop: '2px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        stampBox: {
            width: '72px',
            height: '72px',
            margin: '0 auto 2px',
            overflow: 'hidden',
            background: 'transparent',
        },
        stampPlaceholder: {
            width: '62px',
            height: '62px',
            margin: '0 auto 2px',
            border: '2px dashed #9c7a2e',
            borderRadius: '50%',
            textAlign: 'center',
            lineHeight: '62px',
            fontSize: '8px',
            color: '#bbb',
            fontStyle: 'italic',
        },
        sigRow: {
            fontSize: '10px',
            color: '#1a3a6b',
            marginBottom: '13px',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        sigLineInline: {
            width: '110px',
            borderBottom: '1px solid #1a3a6b',
            display: 'inline-block',
            marginLeft: '6px',
            height: '46px',
            verticalAlign: 'middle',
            overflow: 'hidden',
        },
        sigCaptionBottom: {
            fontSize: '14px',
            color: '#1a3a6b',
            fontWeight: 700,
            fontStyle: 'italic',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        authorityInfo: {
            fontSize: '10px',
            color: '#1a3a6b',
            fontStyle: 'italic',
            lineHeight: 1.4,
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
        },
        bottomBand: {
            marginTop: '4px',
            background: '#ffffff',
            textAlign: 'left',
        },
        addressText: {
            fontSize: '13px',
            color: '#1a3a6b',
            fontWeight: 700,
            fontStyle: 'italic',
            textTransform: 'uppercase',
            fontFamily: '"Noto Sans Devanagari", "NotoSansDevanagari", sans-serif',
            textAlign: 'center',
            padding: '3px 0',
        },
    };

    return (
        <>
            <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&display=swap" rel="stylesheet" />

            <div style={styles.certificate}>
                <div style={styles.borderOuter}>
                    <div style={styles.borderInner}>
                        {/* Top Band */}
                        <div style={styles.topBand}>
                            {showMarathi && (
                                <>
                                    <div style={styles.titleMarathi}>विवाह प्रमाणपत्र</div>
                                    <div style={styles.sub}>हिंदू विवाह कायदा १९५५ (कलम ०७ पहा)</div>
                                    <div style={styles.regNo}>रजि. नं. २०२०६००३१४०३३९००</div>
                                </>
                            )}
                            {showMarathi && showEnglish && <div style={styles.centerDivider}></div>}
                            {showEnglish && (
                                <>
                                    <div style={styles.titleEnglish}>
                                        {showMarathi ? 'CERTIFICATE OF MARRIAGE' : 'Certificate of Marriage'}
                                    </div>
                                    <div style={styles.sub}>Hindu Marriage Act 1955 (See Section 07)</div>
                                    <div style={styles.regNo}>Reg. No. 2020600314033900</div>
                                </>
                            )}
                        </div>

                        <div style={styles.goldDivider}></div>

                        {/* Certificate Number */}
                        <div style={styles.certNumRow}>
                            {/* <span>प्रमाणपत्र क्र. <strong>{toMarathiNumerals(data.certificate_no || '-')}</strong></span>
                            <span>Certificate No. <strong>{data.certificate_no || '-'}</strong></span> */}
                        </div>

                        {/* Photos Row */}
                        <table style={styles.photoTable}>
                            <tbody>
                                <tr>
                                    {/* Groom Photo */}
                                    <td style={styles.photoCellLeft}>
                                        <div style={styles.photoContainer}>
                                            <div style={styles.photoFrame}>
                                                {husbandPhotoUrl ? (
                                                    <img src={husbandPhotoUrl} alt="Groom" style={{ width: '140px', height: '150px', objectFit: 'cover' }} />
                                                ) : (
                                                    <span style={{ fontSize: '8px', color: '#aaa', fontStyle: 'italic', display: 'inline-block', verticalAlign: 'middle', lineHeight: '1.3' }}>वर<br/>Groom</span>
                                                )}
                                            </div>
                                            {stampUrl && (
                                                <div style={styles.stampOnPhotoRight}>
                                                    <img src={stampUrl} alt="Stamp" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                </div>
                                            )}
                                        </div>
                                        <div style={styles.photoCaption}>वर / Groom</div>
                                      
                                    </td>

                                    {/* Bride Photo */}
                                    <td style={styles.photoCellRight}>
                                        <div style={styles.photoContainer}>
                                            <div style={styles.photoFrame}>
                                                {wifePhotoUrl ? (
                                                    <img src={wifePhotoUrl} alt="Bride" style={{ width: '140px', height: '150px', objectFit: 'cover' }} />
                                                ) : (
                                                    <span style={{ fontSize: '8px', color: '#aaa', fontStyle: 'italic', display: 'inline-block', verticalAlign: 'middle', lineHeight: '1.3' }}>वधू<br/>Bride</span>
                                                )}
                                            </div>
                                            {stampUrl && (
                                                <div style={styles.stampOnPhotoLeft}>
                                                    <img src={stampUrl} alt="Stamp" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                </div>
                                            )}
                                        </div>
                                        <div style={styles.photoCaption}>वधू / Bride</div>
                                        
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Certificate Body */}
                        <div style={{ padding: '2px 0', background: '#ffffff' }}>
                            <div style={styles.marathiBlock}>
                                {/* Marathi Section */}
                                {showMarathi && (
                                    <>
                                        <div style={{ fontWeight: 700, color: '#1a3a6b', marginBottom: '3px' }}>प्रमाणित करण्यात येते की,</div>

                                        <div style={styles.fieldRow}>
                                            <span style={styles.labelText}>पतीचे नाव :</span>
                                            <span style={styles.flWideO}>{getMarathiText('husband_name')}</span>
                                            <span style={{ ...styles.labelText, ...styles.labelInline }}>वय :</span>
                                            <span style={styles.flShort}>{toMarathiNumerals(data.husband_age)} वर्षे</span>
                                        </div>

                                        <div style={styles.fieldRow}>
                                            <span style={styles.labelText}>राहणार :</span>
                                            <span style={styles.flWide}>{getMarathiText('husband_address')}</span>
                                        </div>

                                        <div style={styles.fieldRow}>
                                            <span style={styles.labelText}>आणि पत्नीचे नाव :</span>
                                            <span style={styles.flWide}>{getMarathiText('wife_name')}</span>
                                            <span style={{ ...styles.labelText, ...styles.labelInline }}>वय :</span>
                                            <span style={styles.flShort}>{toMarathiNumerals(data.wife_age)} वर्षे</span>
                                        </div>

                                        <div style={styles.fieldRow}>
                                            <span style={styles.labelText}>राहणार :</span>
                                            <span style={styles.flWide}>{getMarathiText('wife_address')}</span>
                                        </div>

                                        <div style={styles.textLine}>
                                            यांचा विवाह दिनांक :
                                            <span style={styles.flShort}>{toMarathiDateShort(data.marriage_date)}</span>
                                            रोजी {getMarathiText('office_name')}, {getMarathiText('place')} या ठिकाणी विधी संपन्न झाला. त्याची
                                            महाराष्ट्र विवाह मंडळाचे विनियमन आणि विवाह नोंदणी विधेयक, १९९८ अन्वये ठेवण्यात आलेल्या नोंदवहीच्या खंड
                                            क्रमांक :
                                            <span style={styles.flShort}>{toMarathiNumerals(data.volume_no)}</span>
                                            च्या पृष्ठक्रमांक:
                                            <span style={styles.flShort}>{toMarathiNumerals(data.serial_no)}</span> संस्थेकडून हिंदु वैदिक पध्दतीने (हिंदू विवाह कायदा १९५५, कलम ७ नुसार) विवाह संपन्न केला आहे.
                                        </div>

                                        <hr style={{ margin: '5px 0', border: 'none', borderTop: '1px solid #c9971f' }} />
                                    </>
                                )}

                                {/* English Section */}
                                {showEnglish && (
                                    <>
                                        <div style={{ ...styles.engLine, fontWeight: 700, color: '#1a3a6b', marginBottom: '3px' }}>Certified that Marriage Between,</div>

                                        <div style={{ ...styles.fieldRow, ...styles.engLine }}>
                                            <span style={styles.labelText}>Husband's Name:</span>
                                            <span style={styles.flWideO}>{data.husband_name}</span>
                                            <span style={{ ...styles.labelText, ...styles.labelInline }}>Age :</span>
                                            <span style={styles.flShort}>{data.husband_age ? `${data.husband_age} years` : '-'}</span>
                                        </div>

                                        <div style={{ ...styles.fieldRow, ...styles.engLine }}>
                                            <span style={styles.labelText}>Residing Ad.:</span>
                                            <span style={styles.flWideO}>{data.husband_address || '-'}</span>
                                        </div>

                                        <div style={{ ...styles.fieldRow, ...styles.engLine }}>
                                            <span style={styles.labelText}>and Wife's Name:</span>
                                            <span style={styles.flWideO}>{data.wife_name}</span>
                                            <span style={{ ...styles.labelText, ...styles.labelInline }}>Age :</span>
                                            <span style={styles.flShort}>{data.wife_age ? `${data.wife_age} years` : '-'}</span>
                                        </div>

                                        <div style={{ ...styles.fieldRow, ...styles.engLine }}>
                                            <span style={styles.labelText}>Residing Ad.:</span>
                                            <span style={styles.flWideO}>{data.wife_address || '-'}</span>
                                        </div>

                                        <div style={{ ...styles.textLine, ...styles.engLine }}>
                                            Solemnized on
                                            <span style={styles.flShort}>{data.marriage_date ? formatDate(data.marriage_date) : '-'}</span>
                                            at (Place) {data.office_name || 'Shree Sai Vaidik Vivah'}, {data.place || 'Nashik'} of Volume No. <span style={{ ...styles.fl, minWidth: '50px' }}>{data.volume_no || '-'} </span> Serial No. <span style={{ ...styles.fl, minWidth: '50px' }}>{data.serial_no || '-'} </span> of Registered of Marriages Maintained
                                            under the Maharashtra Regulation of Marriage Bureaus and Registration of Marriages Act
                                            1998, Performed under Section 7 of Hindu Marriage Act 1955.
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Bottom Table */}
                        <table style={styles.bottomTable}>
                            <tbody>
                                <tr>
                                    {/* Left: Place, Date, QR */}
                                    <td style={{ width: '30%', padding: '0 4px', verticalAlign: 'top' }}>
                                        <div style={{ marginBottom: '4px' }}>
                                            <span style={styles.detailLabel}>ठिकाण: </span>
                                            <span style={{ ...styles.detailValue, fontFamily: '"NotoSansDevanagari", sans-serif' }}>
                                                {getMarathiText('place')}
                                            </span>
                                        </div>
                                        <div style={{ marginBottom: '4px' }}>
                                            <span style={styles.detailLabel}>Place: </span>
                                            <span style={styles.detailValue}>{data.place || '-'}</span>
                                        </div>
                                        <div style={{ marginBottom: '4px' }}>
                                            <span style={styles.detailLabel}>दिनांक: </span>
                                            {data.certificate_date && (
                                                <span style={{ ...styles.detailValue, fontFamily: '"NotoSansDevanagari", sans-serif' }}>
                                                    {toMarathiDateShort(data.certificate_date)}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ marginBottom: '6px' }}>
                                            <span style={styles.detailLabel}>Date: </span>
                                            <span style={styles.detailValue}>
                                                {data.certificate_date ? formatDate(data.certificate_date) : '-'}
                                            </span>
                                        </div>
                                        <div>
                                            {data.qr_code && (
                                                <>
                                                    <div style={styles.qrBox}>
                                                        <img
                                                            src={resolveUrl(data.qr_code)}
                                                            alt="QR Code"
                                                            style={{ width: '55px', height: '55px', objectFit: 'contain' }}
                                                        />
                                                    </div>
                                                    <div style={styles.qrLabel}>स्कॅन करा / Scan to Verify</div>
                                                </>
                                            )}
                                        </div>
                                    </td>

                                    {/* Middle: Signatures */}
                                    <td style={{ width: '35%', padding: '0 4px', paddingTop: '4px' }}>
                                        <div style={styles.sigRow}>
                                            वर / Groom
                                            <span style={styles.sigLineInline}>
                                                {husbandSigUrl && (
                                                    <img src={husbandSigUrl} alt="Groom Signature" style={{ maxHeight: '44px', objectFit: 'contain' }} />
                                                )}
                                            </span>
                                        </div>
                                        <div style={styles.sigRow}>
                                            वधू / Bride
                                            <span style={styles.sigLineInline}>
                                                {wifeSigUrl && (
                                                    <img src={wifeSigUrl} alt="Bride Signature" style={{ maxHeight: '44px', objectFit: 'contain' }} />
                                                )}
                                            </span>
                                        </div>
                                    </td>

                                    {/* Right: Authority */}
                                    <td style={{ width: '35%', textAlign: 'center', verticalAlign: 'top', padding: '0 4px' }}>
                                        {stampUrl ? (
                                            <div style={styles.stampBox}>
                                                <img src={stampUrl} alt="Stamp" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                            </div>
                                        ) : (
                                            <div style={styles.stampPlaceholder}>
                                                Official<br/>Stamp
                                            </div>
                                        )}
                                        <div style={{ height: '39px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2px' }}>
                                            {authoritySignatureUrl && (
                                                <img src={authoritySignatureUrl} alt="Authority Signature" style={{ maxHeight: '39px', maxWidth: '80px', objectFit: 'contain' }} />
                                            )}
                                        </div>
                                        <div style={styles.sigCaptionBottom}>
                                            Signature
                                            <div style={styles.authorityInfo}>
                                                
                                                <span className='text-sm'>{data.authority_name}</span>
                                                {data.office_name && <div style={{ fontSize: '13px' }}>{data.office_name}</div>}
                                                {data.office_address && <div style={{ fontSize: '13px' }}>{data.office_address}</div>}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Footer Band */}
                        <div style={styles.bottomBand}>
                            <div style={styles.addressText}>
                               Shree Sai Vaidik Vivah, 489, Gharpure Ghat, Ashok Stambh, Nashik-422001
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CertificatePrint;